import { booleanAttribute, DestroyRef, Directive, effect, ElementRef, inject, input } from '@angular/core';

/**
 * A directive that enhances anchor tags with additional functionality.
 * Apply this directive to an `<a>` tag to add link-specific behaviors.
 *
 * @example
 * ```html
 * <a joiiLink href="/path">Link Text</a>
 * <a joiiLink [href]="route" [disabled]="isDisabled">Link Text</a>
 * ```
 */
@Directive({
  selector: 'a[joiiLink]',
  host: {
    '[attr.href]': 'href() ?? null',
    '[attr.target]': 'target()',
    '[attr.rel]': 'rel() ?? null',
    '[attr.download]': 'download() ?? null',
    '[attr.aria-disabled]': 'disabled() ? "true" : null',
    '[class.disabled]': 'disabled()',
    '[style.pointer-events]': 'disabled() ? "none" : null',
    '[style.cursor]': 'disabled() ? "not-allowed" : null',
  },
})
export class LinkDirective {
  private readonly elementRef = inject(ElementRef<HTMLAnchorElement>);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * The URL that the link points to.
   */
  readonly href = input<string | null | undefined>();

  /**
   * Where to display the linked URL.
   * @default '_self'
   */
  readonly target = input<string>('_self');

  /**
   * The relationship of the linked URL.
   */
  readonly rel = input<string | null | undefined>();

  /**
   * Whether the link is disabled.
   * @default false
   */
  readonly disabled = input(false, {
    transform: booleanAttribute,
  });

  /**
   * The download attribute specifies that the target will be downloaded.
   */
  readonly download = input<string | null | undefined>();

  constructor() {
    effect(() => {
      const element = this.elementRef.nativeElement;

      // Prevent navigation when disabled
      if (this.disabled()) {
        element.addEventListener('click', this.preventDefault, { capture: true });
      } else {
        element.removeEventListener('click', this.preventDefault, { capture: true });
      }
    });

    // Cleanup event listener on destroy
    this.destroyRef.onDestroy(() => {
      const element = this.elementRef.nativeElement;
      element.removeEventListener('click', this.preventDefault, { capture: true });
    });
  }

  private preventDefault = (event: Event): void => {
    if (this.disabled()) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
}
